import { inject, Injectable } from '@angular/core';

import { TranslocoService } from '@jsverse/transloco';
import { catchError, map, type Observable, of, take } from 'rxjs';

import { DEFAULT_SEO } from './default-seo.const';
import { SeoService } from './seo.service';

import type { SupportedLanguageCode } from '../translate/languages';

export interface IDefaultSeoTexts {
  readonly title: string;
  readonly description: string;
}

const EMPTY_SEO_TEXTS: IDefaultSeoTexts = { title: '', description: '' };

@Injectable({ providedIn: 'root' })
export class DefaultSeoService {
  readonly #seoService = inject(SeoService);
  readonly #translocoService = inject(TranslocoService);

  /**
   * The caller names the language instead of the active UI one: a link preview has to match the
   * language of the link path even where a cookie picks the active language.
   */
  selectTexts(lang: SupportedLanguageCode): Observable<IDefaultSeoTexts> {
    return this.#translocoService.selectTranslation(lang).pipe(
      take(1),
      map(() => ({
        title: this.#translocoService.translate<string>(DEFAULT_SEO.titleKey, {}, lang),
        description: this.#translocoService.translate<string>(DEFAULT_SEO.descriptionKey, {}, lang),
      })),
      // Dictionary failed to load: SeoService skips empty values, so SEO never fails the navigation.
      catchError(() => of(EMPTY_SEO_TEXTS)),
    );
  }

  apply(lang: SupportedLanguageCode): Observable<void> {
    return this.selectTexts(lang).pipe(
      map(({ title, description }) => {
        this.#seoService.setTitle(title);
        this.#seoService.setTag('description', description, 'name');
        this.#seoService.setTag('og:title', title, 'property');
        this.#seoService.setTag('og:description', description, 'property');
      }),
    );
  }
}
